import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Linkedin, Twitter, Facebook, Instagram, Megaphone, Copy, Check,
  Loader2, Sparkles, Save, Trash2, Clock, CheckCheck,
} from "lucide-react";

const PLATFORMS = [
  { key: "linkedin",  label: "LinkedIn",  Icon: Linkedin,  color: "bg-sky-600",       accent: "text-sky-300",     limit: 1200 },
  { key: "x",         label: "X",         Icon: Twitter,   color: "bg-slate-700",     accent: "text-slate-200",   limit: 280 },
  { key: "facebook",  label: "Facebook",  Icon: Facebook,  color: "bg-blue-700",      accent: "text-blue-300",    limit: 600 },
  { key: "instagram", label: "Instagram", Icon: Instagram, color: "bg-fuchsia-600",   accent: "text-fuchsia-300", limit: 2200 },
  { key: "tiktok",    label: "TikTok",    Icon: Megaphone, color: "bg-rose-700",      accent: "text-rose-300",    limit: 300 },
];
const PLATFORM_BY_KEY = Object.fromEntries(PLATFORMS.map((p) => [p.key, p]));

// A few topic prompts tied to the brand positioning — operator can edit.
const TOPIC_SUGGESTIONS = [
  "Why your fastest tech shouldn't earn the same as your slowest",
  "The hidden cost of pure-hourly pay for a 5-person crew",
  "What production pay actually looks like on a weekly paystub",
  "A story about a lead tech who almost quit — until we fixed how he was paid",
  "FLSA overtime for commission-paid field crews (the mistake most owners make)",
  "Hourly vs commission vs higher-of-both — which pays out when",
  "The one Friday-night ritual I don't miss since switching off paper payroll",
  "What a tech sees on their paystub when it's calculated in real time",
];

export default function GrowthSocialPosts() {
  const [tab, setTab] = useState("linkedin");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Generator state
  const [topic, setTopic] = useState("");
  const [includeCta, setIncludeCta] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null); // { body, hashtags, confidence }
  const [draftBody, setDraftBody] = useState("");
  const [saving, setSaving] = useState(false);

  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/growth/social-posts", { params: { platform: tab } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const spec = PLATFORM_BY_KEY[tab];

  const generate = async () => {
    setGenerating(true);
    setDraft(null); setDraftBody("");
    try {
      const { data } = await api.post("/growth/social-posts/draft", {
        platform: tab,
        topic: topic.trim() || null,
        include_cta: includeCta,
      });
      setDraft(data);
      const combined = (data.body || "") + (data.hashtags?.length ? "\n\n" + data.hashtags.join(" ") : "");
      setDraftBody(combined);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setGenerating(false); }
  };

  const saveDraft = async () => {
    if (!draftBody.trim()) return toast.error("Nothing to save.");
    setSaving(true);
    try {
      await api.post("/growth/social-posts", {
        platform: tab, body: draftBody.trim(), topic: topic || null,
      });
      toast.success("Saved to queue.");
      setDraft(null); setDraftBody(""); setTopic("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const copyToClipboard = async (text, id = "draft") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success("Copied — paste into your account.");
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 2000);
    } catch {
      toast.error("Browser blocked the clipboard. Copy manually.");
    }
  };

  const markPosted = async (id) => {
    try {
      await api.post(`/growth/social-posts/${id}/mark-posted`);
      toast.success("Marked as posted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this draft?")) return;
    try {
      await api.delete(`/growth/social-posts/${id}`);
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const bodyLen = draftBody.length;
  const overLimit = bodyLen > spec.limit;

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-5xl mx-auto" data-testid="growth-social-posts">
      <div>
        <h1 className="text-3xl font-extrabold text-white tracking-tight flex items-center gap-2">
          <Megaphone className="h-7 w-7 text-indigo-400" /> Owner's Social Post Queue
        </h1>
        <p className="text-slate-400 text-sm mt-1 max-w-3xl">
          AI drafts the post. You review, hit <b>Copy</b>, and paste into your own account. Zero API cost, zero ToS risk —
          your identity, your voice, no automated publishing. Hit <b>Mark as posted</b> once it's live so attribution flows.
        </p>
      </div>

      {/* Platform tabs */}
      <div className="flex flex-wrap gap-2">
        {PLATFORMS.map(({ key, label, Icon, color }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`platform-tab-${key}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-extrabold transition-all ${
              tab === key
                ? `${color} text-white shadow-lg ring-2 ring-white/20`
                : "bg-slate-800 text-slate-300 hover:bg-slate-700"
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {/* Generator */}
      <Card className="bg-gradient-to-br from-indigo-500/5 via-violet-500/5 to-slate-900 border border-indigo-500/30">
        <CardContent className="p-5 space-y-4">
          <h2 className="text-white font-extrabold text-lg flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-300" /> Draft a post for {spec.label}
          </h2>

          <div>
            <Label className="text-xs text-slate-400">Topic (optional — pick a suggestion or write your own)</Label>
            <Input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. Why your fastest tech shouldn't earn the same as your slowest"
              className="bg-slate-800 border-slate-700 mt-1"
              data-testid="topic-input"
            />
            <div className="flex gap-1.5 flex-wrap mt-2">
              {TOPIC_SUGGESTIONS.slice(0, 6).map((t) => (
                <button
                  key={t}
                  onClick={() => setTopic(t)}
                  className="text-[11px] px-2 py-1 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700"
                  data-testid={`topic-suggestion-${t.slice(0, 20)}`}
                >
                  {t.length > 50 ? t.slice(0, 50) + "…" : t}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Switch checked={includeCta} onCheckedChange={setIncludeCta} id="cta-toggle" data-testid="cta-toggle" />
            <Label htmlFor="cta-toggle" className="text-sm text-slate-300 cursor-pointer">
              Include CTA line (<code className="text-indigo-300">productionpaytracker.com</code>)
            </Label>
          </div>

          <Button
            onClick={generate}
            disabled={generating}
            className="bg-indigo-600 hover:bg-indigo-500 font-extrabold"
            data-testid="generate-btn"
          >
            {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {draft ? "Regenerate" : "Generate draft"}
          </Button>

          {/* Draft output */}
          {draft && (
            <div className="space-y-3 pt-4 border-t border-slate-800" data-testid="draft-output">
              <Textarea
                rows={tab === "x" ? 4 : tab === "linkedin" || tab === "instagram" ? 10 : 6}
                value={draftBody}
                onChange={(e) => setDraftBody(e.target.value)}
                className="bg-slate-950 border-slate-700 text-slate-100 font-mono text-sm"
                data-testid="draft-body"
              />
              <div className="flex items-center justify-between text-xs">
                <span className={`font-extrabold ${overLimit ? "text-rose-300" : "text-slate-400"}`} data-testid="char-count">
                  {bodyLen} / {spec.limit} chars
                  {overLimit && " — over limit"}
                </span>
                <span className="text-slate-500">AI confidence {Math.round((draft.confidence || 0) * 100)}%</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => copyToClipboard(draftBody, "fresh")}
                  disabled={overLimit}
                  className="bg-emerald-600 hover:bg-emerald-500"
                  data-testid="copy-fresh-btn"
                >
                  {copiedId === "fresh" ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                  {copiedId === "fresh" ? "Copied!" : "Copy to clipboard"}
                </Button>
                <Button
                  onClick={saveDraft}
                  disabled={saving || overLimit || !draftBody.trim()}
                  variant="outline"
                  className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"
                  data-testid="save-draft-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                  Save to queue
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Saved queue for this platform */}
      <div>
        <h2 className="text-sm font-extrabold uppercase tracking-widest text-slate-400 mb-3">
          Queue · {spec.label} ({items.length})
        </h2>
        {loading ? (
          <div className="text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <Card className="bg-slate-900/60 border-slate-800 border-dashed">
            <CardContent className="p-8 text-center text-slate-400 text-sm">
              No saved {spec.label} drafts. Generate one above and hit <b>Save to queue</b>.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((p) => (
              <Card key={p.id} className="bg-slate-900/60 border-slate-800" data-testid={`post-row-${p.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      {p.posted ? (
                        <span className="inline-flex items-center gap-1 text-emerald-300 font-extrabold uppercase tracking-widest text-[10px]"><CheckCheck className="h-3 w-3" /> Posted {new Date(p.posted_at).toLocaleDateString()}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-300 font-extrabold uppercase tracking-widest text-[10px]"><Clock className="h-3 w-3" /> Draft</span>
                      )}
                      {p.topic && <span className="text-slate-500 truncate max-w-xs">· {p.topic}</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        onClick={() => copyToClipboard(p.body, p.id)}
                        className="bg-emerald-600 hover:bg-emerald-500 h-7 text-xs"
                        data-testid={`copy-${p.id}`}
                      >
                        {copiedId === p.id ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                        {copiedId === p.id ? "Copied!" : "Copy"}
                      </Button>
                      {!p.posted && (
                        <Button
                          size="sm"
                          onClick={() => markPosted(p.id)}
                          variant="outline"
                          className="border-slate-700 bg-slate-800 text-slate-200 h-7 text-xs"
                          data-testid={`mark-posted-${p.id}`}
                        >
                          <CheckCheck className="h-3 w-3 mr-1" /> Mark posted
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => deletePost(p.id)}
                        className="h-7 w-7 text-slate-500 hover:text-rose-300 hover:bg-rose-500/10"
                        data-testid={`delete-${p.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <pre className="whitespace-pre-wrap font-sans text-sm text-slate-200 bg-slate-950 border border-slate-800 rounded-lg p-3">
{p.body}
                  </pre>
                  <p className="text-[10px] text-slate-500">
                    {p.body.length} chars · created {new Date(p.created_at).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
